'use client'
import { type FC, type MouseEvent, memo, useEffect } from 'react'
import styled from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { Img } from '@/components'
import { getIcon, type TypeAppIcon } from '@/utils'
import { mq, presets } from '@/styles'

type TypeToast = 'info' | 'success' | 'warning' | 'error' | 'loading'
export interface iToastProps {
  type: TypeToast
  message: string
  closeNeeded?: boolean
  icon?: TypeAppIcon
  onClose?: (e?: MouseEvent<HTMLButtonElement>) => void
  timeout?: number
	asAlert?: boolean
  blockInteraction?: boolean
}

export const Toast: FC<iToastProps> = memo(({ message, type, icon, timeout = 5000, closeNeeded = false, onClose, asAlert = false }) => {
	useEffect(() => {
		if (closeNeeded) return
		const timer = setTimeout(() => onClose?.(), timeout)
		return () => clearTimeout(timer)
	}, [timeout, closeNeeded, onClose])

	const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		onClose?.()
	}

	return (
		<Div
			data-type={type}
			role='alert'
			aria-relevant='additions'
			variants={asAlert ? alertVariants : toastVariants}
			initial='initial'
			animate='animate'
			exit='exit'
			className={`toast${asAlert ? ' like-alert' : ''}`}
		>
			{icon && <Img media={getIcon(icon as TypeAppIcon)} />}
			<p dangerouslySetInnerHTML={{ __html: message }} />
			{closeNeeded && (
				<button onClick={handleClose} name='Cerrar' aria-label='Cerrar'>
					<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'><g transform='matrix(-.707107 -.707107 .707107 -.707107 8 22.142136)' stroke='currentColor' fill='none' strokeLinecap='round' strokeWidth='2.036'><path d='M.909 10h18.183'/><path d='M10 19.091V.909'/></g></svg>
				</button>
			)}
		</Div>
	)
})

const Div = styled(motion.div)`
  ${presets.font16}
  align-items: center;
  box-shadow: 0 0 10px var(--color-box-shadow);
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
	min-height: 35px;
  padding: 4px 4px 4px 16px;
  pointer-events: auto;
  max-width: 100dvw;
	width: auto;
  z-index: 999;

  ${mq.greaterThan('nexus7')} {
		max-width: 600px;
		min-width: 300px;
		width: fit-content;
  }

	&.like-alert {
		--toast-border-radius: 4px;
		align-items: flex-start;
		position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    min-height: 100px;
    border-radius: var(--toast-border-radius);
    padding: 40px 40px 32px 16px;

		p {
			max-width: none;
		}

		button {
			height: 40px;
			position: absolute;
			right: 0;
			top: 0;
		}
	}

	&[data-type='error'],
	&[data-type='warning'],
	&[data-type='success'] {
		color: var(--color-background);
	}

	&[data-type='info'] {
		background-color: var(--color-text--yellow);
		color: var(--color-text--darker);
	}

	&[data-type='warning'] {
		background-color: var(--color-warning);
	}
	
	&[data-type='success'] {
		background-color: #5F895E;
	}

	&[data-type='error'] {
		background-color: var(--color-text--darker);
	}

  p {
    flex-grow: 2;
    flex-shrink: 0;
		max-width: calc(100% - 45px);
    text-align: left;
    width: auto;

		${mq.greaterThan('tablet')} {
			text-align: left;
		}

		> span {
			font-weight: 500;
			margin-right: 10px;
			text-transform: uppercase;
		}
  }

	button {
		align-items: center;
		display: flex;
		flex-shrink: 0;
    height: 35px;
		justify-content: center;
    width: 40px;
		transition: 300ms opacity ease-out;

		&:hover {
			img,
			svg {
				opacity: 1;
			}
		}

		img,
		svg {
			opacity: .5;
			transition: 300ms opacity ease-out;
			width: 12px;
		}
	}
`

const toastVariants: Variants = {
  initial: { opacity: 0, x: '100%' },
  animate: { opacity: 1, x: 0, transition: { type: 'tween', duration: .4, ease: 'easeOut' } },
  exit: { opacity: 0, x: '100%', transition: { type: 'tween', duration: .3, ease: 'easeIn' } },
}

const alertVariants: Variants = {
  initial: { opacity: 0, x: '-50%', y: '10px' },
  animate: { opacity: 1, x: '-50%', y: '-50%', transition: { type: 'tween', duration: .4, ease: 'easeOut' } },
  exit: { opacity: 0, x: '-50%', y: '-20px', transition: { type: 'tween', duration: .3, ease: 'easeIn' } },
}

export const ToastWrapper = styled.aside`
	--toast-border-radius: 10px;
	display: flex;
	flex-direction: column;
	height: 100vh;
  bottom: 0;
  left: 0;
  max-width: 100dvw;
  max-width: 100vw;
  pointer-events: none;
  position: fixed;
  right: 0;
  transition: 300ms all linear;
  user-select: none;
  width: 100%;
  z-index: 9000;

  .toast {
    &:last-child {
      margin-bottom: 0;
    }
  }

  &[data-has-toasts='true'] {
		user-select: auto;
  }

  &[data-block-interaction='true'] {
    background-color: rgba(0,0,0, .25);
		pointer-events: auto;
		user-select: auto;
  }

	&[data-position='top-left'] {
		align-items: flex-start;
		justify-content: flex-start;
	}

	&[data-position='top-right'] {
		align-items: flex-end;
		justify-content: flex-start;
	}

	&[data-position='bottom-left'] {
		align-items: flex-start;
		justify-content: flex-end;
	}

	&[data-position='bottom-right'] {
		align-items: flex-end;
		justify-content: flex-end;
	}

	&[data-position='top-left'],
	&[data-position='bottom-left'] {
		.toast {
			${mq.greaterThan('nexus7')} {
				border-bottom-right-radius: var(--toast-border-radius);
				border-top-right-radius: var(--toast-border-radius);
			}
		}
	}

	&[data-position='top-right'],
	&[data-position='bottom-right'] {
		.toast {
			${mq.greaterThan('nexus7')} {
				border-bottom-left-radius: var(--toast-border-radius);
				border-top-left-radius: var(--toast-border-radius);
			}
		}
	}
`
